import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Check, Clock, Phone, Sparkles, CalendarDays, Loader2, UserPlus, ArrowRight, Trash2 } from "lucide-react";

interface WaitlistEntry {
  id: number;
  guest_name: string;
  phone?: string;
  service_id?: number;
  service_name?: string;
  preferred_date?: string;
  preferred_time?: string;
  notes?: string;
  status: "waiting" | "contacted" | "converted" | "cancelled";
  created_at: string;
}
interface SpaService { id: number; name: string; duration: number; price: number; }

const STATUS_COLOR: Record<string, string> = {
  waiting:   "#fbbf24",
  contacted: "#818cf8",
  converted: "#34d399",
  cancelled: "#f87171",
};

function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }

export default function SpaWaitlist({ API, services, onConvertToBooking }: { API: string; services: SpaService[]; onConvertToBooking: (entry: WaitlistEntry) => void }) {
  const [list, setList]       = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ guest_name: "", phone: "", service_id: "", preferred_date: "", preferred_time: "", notes: "" });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/waitlist`, { headers: authH() }); setList(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const add = async () => {
    if (!form.guest_name.trim()) return;
    setSaving(true);
    try {
      const svc = services.find(s => s.id === Number(form.service_id));
      await axios.post(`${API}/waitlist`, { ...form, service_id: form.service_id ? Number(form.service_id) : undefined, service_name: svc?.name }, { headers: authH() });
      setShowAdd(false); setForm({ guest_name: "", phone: "", service_id: "", preferred_date: "", preferred_time: "", notes: "" }); fetch();
    } catch { /**/ } finally { setSaving(false); }
  };

  const patch = async (id: number, status: string) => {
    await axios.put(`${API}/waitlist/${id}`, { status }, { headers: authH() });
    setList(l => l.map(e => e.id === id ? { ...e, status: status as WaitlistEntry["status"] } : e));
  };

  const del = async (id: number) => {
    if (!confirm("Remove from waitlist?")) return;
    await axios.delete(`${API}/waitlist/${id}`, { headers: authH() });
    setList(l => l.filter(e => e.id !== id));
  };

  const active = list.filter(e => e.status === "waiting" || e.status === "contacted");
  const done   = list.filter(e => e.status === "converted" || e.status === "cancelled");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#F2EBD9" }}>Waitlist</div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{active.length} waiting</div>
        </div>
        <button className="spa-btn-primary" onClick={() => setShowAdd(true)}><Plus size={14} /> Add Guest</button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="spa-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">Add to Waitlist</h2>
              <button className="spa-modal-close" onClick={() => setShowAdd(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Guest Name *</label><input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))} placeholder="Full name" /></div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+20…" /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Service</label>
                  <select value={form.service_id} onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}>
                    <option value="">Any service</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="spa-form-row" style={{ marginTop: 12 }}>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Preferred Date</label><input type="date" value={form.preferred_date} onChange={e => setForm(f => ({ ...f, preferred_date: e.target.value }))} /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Preferred Time</label><input type="time" value={form.preferred_time} onChange={e => setForm(f => ({ ...f, preferred_time: e.target.value }))} /></div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any special requests…" /></div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={add} disabled={saving || !form.guest_name.trim()}>
                {saving ? <Loader2 size={14} className="spa-spinner" /> : <UserPlus size={14} />} Add to Waitlist
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div>
      ) : active.length === 0 ? (
        <div className="spa-fully-booked"><Clock size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No guests waiting</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {active.map(e => (
            <div key={e.id} style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLOR[e.status], marginTop: 6, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 14 }}>{e.guest_name}</div>
                <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginTop: 3, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {e.service_name && <span><Sparkles size={11} style={{ display: "inline", marginRight: 3 }} />{e.service_name}</span>}
                  {e.phone && <span><Phone size={11} style={{ display: "inline", marginRight: 3 }} />{e.phone}</span>}
                  {e.preferred_date && <span><CalendarDays size={11} style={{ display: "inline", marginRight: 3 }} />{e.preferred_date}{e.preferred_time ? ` · ${e.preferred_time}` : ""}</span>}
                </div>
                {e.notes && <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)", marginTop: 4 }}>{e.notes}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {e.status === "waiting" && <button className="spa-action-btn" style={{ fontSize: 11, padding: "4px 10px", borderColor: "rgba(129,140,248,0.3)", color: "#818cf8" }} onClick={() => patch(e.id, "contacted")}><Phone size={11} /> Contacted</button>}
                <button className="spa-action-btn success" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => { patch(e.id, "converted"); onConvertToBooking(e); }}><ArrowRight size={11} /> Book</button>
                <button className="spa-action-btn danger" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => del(e.id)}><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.3)", marginBottom: 10 }}>Resolved</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {done.map(e => (
              <div key={e.id} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, opacity: 0.6 }}>
                <span style={{ color: STATUS_COLOR[e.status], fontSize: 11, fontWeight: 700 }}>{e.status.toUpperCase()}</span>
                <span style={{ color: "rgba(242,235,217,0.5)", fontSize: 13 }}>{e.guest_name}</span>
                {e.service_name && <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>{e.service_name}</span>}
                <button className="spa-action-btn danger" style={{ marginLeft: "auto", fontSize: 11, padding: "3px 8px" }} onClick={() => del(e.id)}><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
